<template>
  <div class="card">
    <div class="card-header flex-wrap pt-6 pb-0">
      <div class="card-title">
        <div class="card-label">
          <h3 class="card-label fs-2">{{ $t('projects.reports.report_list') }}</h3>
        </div>
      </div>
      <div class="card-toolbar">
        <ButtonDropdownFilter
          :search="true"
          :options="Object.values(allUsers)"
          value-field="id"
          name-field="fullname"
          v-model="query.user"
        />
        <ButtonDropdownFilter
          :search="true"
          :options="Object.values(stepProject)"
          value-field="id"
          name-field="title"
          v-model="query.step"
        />
        <ButtonDropdownFilter
          :search="false"
          :options="Object.values(allStatus)"
          value-field="value"
          :name-field="locale"
          v-model="query.status"
        />
        <DateRangePicker id="report-daterangepicker" @update:date="handleRangeDate" />
        <Button v-if="$can(abilities.PROJECT_REPORTING_CREATE)" type="new" btn-class="btn-sm me-2" btn-text="projects.reports.new_report" @click="openReportModal"></Button>
      </div>
    </div>
    <div class="card-body">
      <div class="row">
        <table class="table align-middle table-row-bordered fs-6 gy-3 gx-4" id="kt_reports_datatables">
          <thead>
            <tr class="fw-bold fs-6" style="background-color: #F2F3F4;">
              <th class="min-w-100px">{{ $t('projects.reports.title') }}</th>
              <th class="min-w-100px">{{ $t('projects.reports.start_at') }} </th>
              <th class="min-w-100px">{{ $t('projects.reports.user') }} </th>
              <th class="min-w-100px">{{ $t('projects.reports.step') }}</th>
              <th class="min-w-100px">{{ $t('projects.reports.status') }}</th>
              <th class="min-w-100px">{{ $t('projects.actions') }}</th>
            </tr>
          </thead>
        </table>
      </div>
    </div>
  </div>

  <ReportModal id="report_modal_create" @load="fetchDatas" :projectSteps="projectSteps"/>
  <ReportModal id="update_report_modal_create" :edit="true" :form="formUpdateData" @load="fetchDatas" :projectSteps="projectSteps"/>
  <FilesAttachModal id="files_modal" :files="formUpdateData.attachFiles"/>
  <DetailReportModal id="detail_report_modal" :detail="formUpdateData.description"/>
</template>

<script setup> 
import {onMounted, ref, watch, computed} from "vue";
import {useStore} from "vuex";
import {useLocales} from "@/composables/locales";
import Button from "@/components/Button";
import {useRoute} from 'vue-router';
import {useSwal} from "@/composables/useSwal";
import { useI18n } from 'vue-i18n'
import {BASE_URL} from "@/services/api";
import {Theme} from "@/composables/theming";
import Ls from "@/services/ls";
import ReportModal from "./modals/ReportModal.vue";
import DateRangePicker from "@/components/DateRangePicker.vue";
import ButtonDropdownFilter from "@/components/ButtonDropdownFilter.vue";
import i18n from "@/plugins/i18n";
import DetailReportModal from "./modals/DetailReport.vue"
import abilities from "@/composables/abilities";
import { useHasAbility } from "@/composables/hasAbility";
import FilesAttachModal from "@/components/FilesAttachModal.vue";
import {$dateFormatter} from "@/mixins/Helpers";

const store = useStore()
const route = useRoute()
const {showSwal} = useSwal()
const {can} = useHasAbility()
const { t } = useI18n({ useScope: 'global' })
const {locale, datatableLocaleUrl} = useLocales()

const table = ref(null)
const formUpdateData = ref({
  id: '', 
  project_id: route.params.id, 
  title: '', 
  description: '', 
  category_step_id: '', 
  task_id: '', 
  start_date: '',
  end_date: '',
  files: [],
  attachFiles: [],
  delete_files: []
})
const query = ref({
  start_date: '',
  end_date: '',
  step: '',
  user: '',
  status: ''
})

const projectSteps = computed(() => store.getters['projectSteps/getProjectSteps'])
const stepProject = computed(() => {
  return [{ id: '', title: t('projects.reports.all_steps') }, ...(projectSteps.value || [])];
});
const reportStatus = computed(() => store.getters['projects/getReportStatus'])
const allStatus = computed(() => {
  return [{ value: '', fr: t('projects.reports.all_status'), en: t('projects.reports.all_status') }, ...(reportStatus.value || [])];
});
const users = computed(() => store.getters['user/getUsers'])
const allUsers = computed(() => {
  return [{ id: '', fullname: t('projects.reports.all_users') }, ...(users.value || [])];
});

watch(() => locale.value, (value) => {
  fetchDatas()
}, { deep: true })

watch(() => query.value, (newValue) => {
  if(table.value){
    table.value.ajax.url(`${BASE_URL}/get/project/report/datatables?start_date=`+ query.value.start_date + 
    '&end_date=' + query.value.end_date + '&user_id=' + query.value.user + 
    '&category_step_id=' + query.value.step + '&status=' + query.value.status + '&project_id=' +route.params.id).load()
  }
}, { deep: true })

watch(() => route.query.tab, (value) => {
  if(table.value){
    table.value.ajax.url(`${BASE_URL}/get/project/report/datatables?start_date=`+ query.value.start_date + 
    '&end_date=' + query.value.end_date + '&user_id=' + query.value.user + 
    '&category_step_id=' + query.value.step + '&status=' + query.value.status + '&project_id=' +route.params.id).load()
  }
}, { deep: true })

onMounted(() => {
  store.dispatch('projectSteps/loadProjectSteps', route.params.id)
  store.dispatch('projects/loadReportStatus')
  store.dispatch("user/loadUsers")
  store.dispatch('projects/loadProjectStepTasks', route.params.id)
  fetchDatas()
})

function openReportModal(){
  $('#report_modal_create').modal('toggle')
}

function handleRangeDate (range) {
  query.value.start_date = range.start_date
  query.value.end_date = range.end_date
  if(table.value) {
    table.value.ajax.url(`${BASE_URL}/get/project/report/datatables?start_date=`+ query.value.start_date + 
    '&end_date=' + query.value.end_date + '&user_id=' + query.value.user + 
    '&category_step_id=' + query.value.step + '&status=' + query.value.status + '&project_id=' +route.params.id).load()
  }
}

function fetchDatas() {
  const AUTH_TOKEN = Ls.get('auth.token')
  const pageLimit = Ls.get('pageLength')

  table.value = $("#kt_reports_datatables").DataTable({
    destroy: true,
    responsive: !0,
    searchDelay: 500,
    processing: !0,
    serverSide: !0,
    pageLength: pageLimit ||  5,
    lengthMenu: [5, 10, 25, 50, 75, 100 ],
    order: [[ 0, 'desc']],
    ajax: {
      url: BASE_URL + `/get/project/report/datatables?start_date=${query.value.start_date}&end_date=${query.value.end_date}&user_id=${query.value.user}&category_step_id=${query.value.step}&status=${query.value.status}&project_id=${route.params.id}`,
      type: "GET",
      beforeSend: function (request) {
        request.setRequestHeader("Authorization", "Bearer " + AUTH_TOKEN);
        request.setRequestHeader("Company", Ls.get('current.company'));
      }
    },
    columns: [
    { data: "title" }, { data: "start_date" }, { data: "user" }, { data: "categoryStep" }, { data: "status" }, { data: "Actions", responsivePriority: -1 }
    ],
    columnDefs: [
      {
        targets: -6,
        order: "desc",
        className: "dt-start text-start clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + t  + "</span>";
        },
      },
      {
        targets: -5,
        order: "desc",
        className: "dt-start text-start clickable" ,
        render: function (t, a, e, l) {
          let elt = (t == null) ? '' : t
          return '<span data-id="'+ e.id +'">' + $dateFormatter(elt, locale.value, false, true) + "</span>";
        },
      },
      {
        targets: -4,
        order: "desc",
        className: "dt-start text-start clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + t + "</span>";
        },
      },
      {
        targets: -3,
        orderable: false,
        className: "dt-start text-start" ,
        render: function (t, a, e, l) {
          let elt = (t == null) ? '' : t
          return '<span data-id="'+ e.id +'">' + elt + "</span>";
        },
      },
      {
        targets: -2,
        order: "desc",
        className: "dt-start text-start clickable" ,
        render: function (t, a, e, l) {
          let status = getStatusName(t)
          const locale = i18n.global.locale.value
          let color = status.color
          status = status[locale]
          return '<span class="badge badge-light-' + color +'" style="font-size:13px; margin:3px; padding:5px"><span style="margin-left: 5px" class="valueRow' + l.row + ' label label-success label-inline font-weight-bolder mr-2 edit_value_btn">' + status + '</span></span>'
        },
      },
      {
        width: "15%",
        targets: -1,
        "className": "dt-end text-end",
        title: "Actions",
        orderable: !1,
        render: (t, a, e, l) => {
          let render = ""

          
          if(can(abilities.PROJECT_REPORTING_VIEW)){
            render += `<a href="javascript:;" data-id="${e.id}" data-description="${e.description}" class="btn btn-sm btn-clean btn-icon view_btn" title="${i18n.global.t('button.view')}"><i data-id="${e.id}" data-description="${e.description}" class="fas fa-eye fs-2" style="color: ${Theme.gray};"></i></a>`;
          }

          if(can(abilities.PROJECT_REPORTING_PUBLISHED)){
            if(e.status == "SEEN_BY_CUSTOMER" || e.status == "NOT_PUBLISHED") {
              render += `<a href="javascript:;" data-id="${e.id}" class="btn btn-sm btn-clean btn-icon publish_btn" title="${i18n.global.t('projects.reports.publish_btn')}"><i data-id="${e.id}" class="fa fa-paper-plane fs-2" style="color: ${Theme.gray};"></i></a>`;
            }
            if(e.status == "PUBLISHED" || e.status == "NOT_PUBLISHED") {
              render += `<a href="javascript:;" data-id="${e.id}" class="btn btn-sm btn-clean btn-icon publish_client_btn" title="${i18n.global.t('projects.reports.publish_client_btn')}"><i data-id="${e.id}" class="far fa-paper-plane fs-2" style="color: ${Theme.gray};"></i></a>`;
            }
          }
          if(can(abilities.PROJECT_REPORTING_UPDATE) && e.has_files){
            render += `<a href="javascript:;" data-id="${e.id}" class="btn btn-sm btn-clean btn-icon file_btn" title="${i18n.global.t('absences.add_file')}"><i data-id="${e.id}" class="bi bi-file-earmark fs-2" style="color: ${Theme.gray};"></i></a>`;
          }
          
          if(e.status == "NOT_PUBLISHED") {
            if(can(abilities.PROJECT_REPORTING_UPDATE)){
              render += `<a href="javascript:;" data-id="${e.id}" class="btn btn-sm btn-clean btn-icon edit_btn" title="${i18n.global.t('button.edit')}"><i data-id="${e.id}" class="fas fa-edit fs-2" style="color: ${Theme.gray};"></i></a>`;
            }
            if(can(abilities.PROJECT_REPORTING_DELETE)){
              render += '<a href="javascript:;" data-id="'+ e.id +'"  class="btn btn-sm btn-clean btn-icon delete_btn" title="'+ i18n.global.t('button.delete') +'"><i  data-id="'+ e.id +'" class="fas fa-trash fs-2"  style="color: '+Theme.gray +'"></i></a>'
            }
          }

          return render
        },
      },
    ],
    language: {
      url: datatableLocaleUrl.value,
    }
  });

  $(document).on("click", "#kt_reports_datatables .delete_btn", (e) => {
    let id = e.target.getAttribute('data-id')
    deleteReport(id)
  });

  $(document).on("click", "#kt_reports_datatables .view_btn", (e) => {
    formUpdateData.value.description = e.target.getAttribute('data-description')
    $('#detail_report_modal').modal('toggle')
  });

  $(document).on("click", "#kt_reports_datatables .edit_btn", (e) => {
    let id = e.target.getAttribute('data-id')
    openDetailModal(id, false)
  });

  $(document).on("click", "#kt_reports_datatables .file_btn", (e) => {
    let id = e.target.getAttribute('data-id')
    openDetailModal(id, true)
  });

  $(document).on("click", "#kt_reports_datatables .publish_btn", (e) => {
    let id = e.target.getAttribute('data-id')
    publishReport(id)
  });

  $(document).on("click", "#kt_reports_datatables .publish_client_btn", (e) => {
    let id = e.target.getAttribute('data-id')
    publishClientReport(id)
  });
}

function openDetailModal(id, getFile){
  if (formUpdateData.value.id === id) {
    if (getFile) {
      $('#files_modal').modal('toggle');
    } else {
      $('#update_report_modal_create').modal('toggle');
    }
  }else{
    store.dispatch('projects/showProjectReport', id)
      .then((res) => {
        let data = res.data?.project_report;
        formUpdateData.value.id = id;
        formUpdateData.value.project_id = route.params.id;
        formUpdateData.value.title = data?.title;
        formUpdateData.value.description = data?.description;
        formUpdateData.value.category_step_id = data?.category_step?.id || '';
        formUpdateData.value.task_id = data?.task?.id || '';
        formUpdateData.value.start_date = data?.start_date;
        formUpdateData.value.end_date = data?.end_date;
        formUpdateData.value.files = [];
        formUpdateData.value.delete_files = [];
        formUpdateData.value.attachFiles = data?.files;

        if (getFile) {
          $('#files_modal').modal('toggle');
        } else {
          $('#update_report_modal_create').modal('toggle');
        }
      })
  }
  
}

function getStatusName(status) {
  const {color, fr, en} = reportStatus.value?.find(el => el.value === status) || ''
  return {color, fr, en}
}

async function deleteReport(id){
  showSwal(
      t('projects.suppression'),
      t('projects.reports.delete_text')
  ).then( async (result) => {
    if (result.value) {
      try {
        store.dispatch('projects/deleteProjectReport', id)
          .then(() => {
            fetchDatas()
            window.toastr['success'](i18n.global.t('projects.reports.deleted_successfully'))
          })
      } catch (e) {
      }
    }
  });
}

async function publishReport(id){
  showSwal(
    '',
    t('projects.reports.publish_text')
  ).then( async (result) => {
    if (result.value) {
      try {
        store.dispatch('projects/publishProjectReport', id)
          .then(() => {
            fetchDatas()
            window.toastr['success'](i18n.global.t('projects.reports.operate_successfully'))
          })
      } catch (e) {
      }
    }
  });
}

async function publishClientReport(id){
  showSwal(
    '',
    t('projects.reports.publish_client_text')
  ).then( async (result) => {
    if (result.value) {
      try {
        store.dispatch('projects/clientPublishProjectReport', id)
          .then(() => {
            fetchDatas()
            window.toastr['success'](i18n.global.t('projects.reports.operate_successfully'))
          })
      } catch (e) {
      }
    }
  });
}
</script>